import React, { useState, useEffect } from 'react';
import axios from 'axios';

const API = process.env.REACT_APP_API_URL;

const useIsMobile = () => {
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  useEffect(() => {
    const handle = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handle);
    return () => window.removeEventListener('resize', handle);
  }, []);
  return isMobile;
};

const formatDate = (dateStr) => {
  const date = new Date(dateStr);
  const now = new Date();
  const diff = Math.floor((now - date) / 1000);
  if (diff < 60) return 'just now';
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  if (diff < 604800) return `${Math.floor(diff / 86400)}d ago`;
  return date.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
};

const longevityLabels = ['', '2hrs', '4hrs', '6hrs', 'All day', 'Next day'];
const sillageLabels = ['', 'Personal', 'Close range', 'Nearby', 'Room filler', 'Trail'];

const Avatar = ({ username, color, size = 36 }) => (
  <div style={{
    width: size, height: size, borderRadius: '50%', background: color || '#c9a96e',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    fontFamily: 'Cormorant Garamond, serif', fontSize: size * 0.38, fontWeight: 500,
    color: '#0a0a0a', flexShrink: 0,
  }}>
    {username?.slice(0, 2).toUpperCase()}
  </div>
);

const RatingDots = ({ value, max = 5 }) => (
  <div style={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
    {Array.from({ length: max }, (_, i) => (
      <span key={i} style={{ width: '5px', height: '5px', borderRadius: '50%', background: i < value ? '#c9a96e' : '#666060', display: 'inline-block' }} />
    ))}
  </div>
);

const FeedEntry = ({ entry, token, currentUser, onNavigate }) => {
  const isMobile = useIsMobile();
  const [liked, setLiked] = useState(entry.liked_by_me);
  const [likeCount, setLikeCount] = useState(parseInt(entry.like_count) || 0);
  const [comments, setComments] = useState([]);
  const [showComments, setShowComments] = useState(false);
  const [commentText, setCommentText] = useState('');
  const [loadingComments, setLoadingComments] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const toggleLike = async () => {
    try {
      if (liked) {
        const res = await axios.delete(`${API}/api/journal/${entry.id}/likes`, { headers: { Authorization: `Bearer ${token}` } });
        setLiked(false); setLikeCount(res.data.like_count);
      } else {
        const res = await axios.post(`${API}/api/journal/${entry.id}/likes`, {}, { headers: { Authorization: `Bearer ${token}` } });
        setLiked(true); setLikeCount(res.data.like_count);
      }
    } catch (err) { console.error(err); }
  };

  const loadComments = async () => {
    if (showComments) { setShowComments(false); return; }
    setLoadingComments(true);
    try {
      const res = await axios.get(`${API}/api/journal/${entry.id}/comments`, { headers: { Authorization: `Bearer ${token}` } });
      setComments(res.data); setShowComments(true);
    } catch (err) { console.error(err); }
    setLoadingComments(false);
  };

  const submitComment = async () => {
    if (!commentText.trim()) return;
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/api/journal/${entry.id}/comments`, { content: commentText }, { headers: { Authorization: `Bearer ${token}` } });
      setComments(prev => [...prev, res.data]); setCommentText('');
    } catch (err) { console.error(err); }
    setSubmitting(false);
  };

  const deleteComment = async (commentId) => {
    try {
      await axios.delete(`${API}/api/journal/comments/${commentId}`, { headers: { Authorization: `Bearer ${token}` } });
      setComments(prev => prev.filter(c => c.id !== commentId));
    } catch (err) { console.error(err); }
  };

  return (
    <div style={{ background: '#0f0f0f', border: '1px solid #1a1a1a', marginBottom: '1px' }}>
      {/* Entry header */}
      <div style={{ padding: isMobile ? '18px 16px 0' : '24px 28px 0', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
          <Avatar username={entry.username} color={entry.avatar_color} size={isMobile ? 32 : 36} />
          <div>
            <span
              style={{ fontFamily: 'Jost, sans-serif', fontSize: '0.75rem', color: '#f5f0eb', cursor: 'pointer', letterSpacing: '0.1em' }}
              onClick={() => onNavigate(`/u/${entry.username}`)}
            >{entry.username ? entry.username.charAt(0).toUpperCase() + entry.username.slice(1) : ''}</span>
            <p style={{ fontFamily: 'Jost, sans-serif', fontSize: '0.62rem', color: '#7a7470', letterSpacing: '0.05em', marginTop: '2px' }}>{formatDate(entry.created_at)}</p>
          </div>
        </div>
        {entry.occasion && !isMobile && (
          <span style={{ fontFamily: 'Jost, sans-serif', fontSize: '0.58rem', letterSpacing: '0.15em', textTransform: 'uppercase', color: '#8a8480', border: '1px solid #1e1e1e', padding: '3px 8px' }}>{entry.occasion}</span>
        )}
      </div>

      {/* Fragrance content */}
      <div style={{ padding: isMobile ? '16px 16px' : '20px 28px', borderBottom: '1px solid #141414' }}>
        <p style={{ fontFamily: 'Jost, sans-serif', fontSize: '0.58rem', letterSpacing: '0.3em', textTransform: 'uppercase', color: '#c9a96e', marginBottom: '4px' }}>{entry.fragrance_brand}</p>
        <p style={{ fontFamily: 'Cormorant Garamond, serif', fontSize: isMobile ? '1.4rem' : '1.6rem', fontWeight: 300, color: '#f5f0eb', marginBottom: '8px', lineHeight: 1.1 }}>{entry.fragrance_name}</p>
        <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', alignItems: 'center' }}>
          {entry.main_accord && <span style={{ fontFamily: 'Jost, sans-serif', fontSize: '0.58rem', letterSpacing: '0.12em', textTransform: 'uppercase', color: '#8a8480', border: '1px solid #1e1e1e', padding: '3px 8px' }}>{entry.main_accord}</span>}
          {entry.mood && <span style={{ fontFamily: 'Jost, sans-serif', fontSize: '0.58rem', letterSpacing: '0.12em', textTransform: 'uppercase', color: '#8a8480', border: '1px solid #1e1e1e', padding: '3px 8px' }}>{entry.mood}</span>}
          {entry.weather && <span style={{ fontFamily: 'Jost, sans-serif', fontSize: '0.58rem', letterSpacing: '0.12em', textTransform: 'uppercase', color: '#8a8480', border: '1px solid #1e1e1e', padding: '3px 8px' }}>{entry.weather}{entry.temperature ? ` · ${entry.temperature}°C` : ''}</span>}
          {isMobile && entry.occasion && <span style={{ fontFamily: 'Jost, sans-serif', fontSize: '0.58rem', letterSpacing: '0.12em', textTransform: 'uppercase', color: '#8a8480', border: '1px solid #1e1e1e', padding: '3px 8px' }}>{entry.occasion}</span>}
        </div>
        {(entry.longevity_rating || entry.sillage_rating) && (
          <div style={{ display: 'flex', gap: isMobile ? '16px' : '24px', marginTop: '16px', flexWrap: 'wrap' }}>
            {entry.longevity_rating && (
              <div>
                <p style={{ fontFamily: 'Jost, sans-serif', fontSize: '0.55rem', letterSpacing: '0.2em', textTransform: 'uppercase', color: '#666060', marginBottom: '5px' }}>Longevity</p>
                <RatingDots value={entry.longevity_rating} />
                <p style={{ fontFamily: 'Jost, sans-serif', fontSize: '0.6rem', color: '#7a7470', marginTop: '3px' }}>{longevityLabels[entry.longevity_rating]}</p>
              </div>
            )}
            {entry.sillage_rating && (
              <div>
                <p style={{ fontFamily: 'Jost, sans-serif', fontSize: '0.55rem', letterSpacing: '0.2em', textTransform: 'uppercase', color: '#666060', marginBottom: '5px' }}>Projection</p>
                <RatingDots value={entry.sillage_rating} />
                <p style={{ fontFamily: 'Jost, sans-serif', fontSize: '0.6rem', color: '#7a7470', marginTop: '3px' }}>{sillageLabels[entry.sillage_rating]}</p>
              </div>
            )}
            {entry.compliments > 0 && (
              <div>
                <p style={{ fontFamily: 'Jost, sans-serif', fontSize: '0.55rem', letterSpacing: '0.2em', textTransform: 'uppercase', color: '#666060', marginBottom: '5px' }}>Compliments</p>
                <p style={{ fontFamily: 'Cormorant Garamond, serif', fontSize: '1.2rem', color: '#c9a96e', lineHeight: 1 }}>{entry.compliments}</p>
              </div>
            )}
          </div>
        )}
        {entry.notes && <p style={{ fontFamily: 'Cormorant Garamond, serif', fontSize: '1rem', fontStyle: 'italic', color: '#5a5a5a', marginTop: '16px', lineHeight: 1.6 }}>"{entry.notes}"</p>}
      </div>

      {/* Like / comment bar */}
      <div style={{ padding: isMobile ? '12px 16px' : '14px 28px', display: 'flex', gap: '20px', alignItems: 'center' }}>
        <button onClick={toggleLike} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, display: 'flex', alignItems: 'center', gap: '6px', fontFamily: 'Jost, sans-serif', fontSize: '0.65rem', letterSpacing: '0.1em', color: liked ? '#c9a96e' : '#7a7470', transition: 'color 0.15s ease' }}>
          {liked ? '♥' : '♡'} {likeCount > 0 ? likeCount : ''}
        </button>
        <button onClick={loadComments} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, display: 'flex', alignItems: 'center', gap: '6px', fontFamily: 'Jost, sans-serif', fontSize: '0.65rem', letterSpacing: '0.1em', color: showComments ? '#c9a96e' : '#7a7470', transition: 'color 0.15s ease' }}>
          ○ {parseInt(entry.comment_count) > 0 ? entry.comment_count : ''} {loadingComments ? '...' : 'comment'}
        </button>
      </div>

      {/* Comments */}
      {showComments && (
        <div style={{ borderTop: '1px solid #141414', padding: isMobile ? '14px 16px' : '16px 28px' }}>
          {comments.length === 0 && <p style={{ fontFamily: 'Jost, sans-serif', fontSize: '0.7rem', color: '#666060', marginBottom: '16px' }}>No comments yet</p>}
          {comments.map(comment => (
            <div key={comment.id} style={{ display: 'flex', gap: '10px', marginBottom: '14px', alignItems: 'flex-start' }}>
              <Avatar username={comment.username} color={comment.avatar_color} size={28} />
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
                  <span style={{ fontFamily: 'Jost, sans-serif', fontSize: '0.68rem', color: '#a09890', marginRight: '8px', letterSpacing: '0.1em' }}>{comment.username ? comment.username.charAt(0).toUpperCase() + comment.username.slice(1) : ''}</span>
                  <span style={{ fontFamily: 'Jost, sans-serif', fontSize: '0.6rem', color: '#666060', flexShrink: 0 }}>{formatDate(comment.created_at)}</span>
                </div>
                <p style={{ fontFamily: 'Jost, sans-serif', fontSize: '0.78rem', color: '#6b6460', fontWeight: 300, marginTop: '3px', lineHeight: 1.5, wordBreak: 'break-word' }}>{comment.content}</p>
              </div>
              {comment.user_id === currentUser?.id && (
                <button onClick={() => deleteComment(comment.id)} style={{ background: 'none', border: 'none', color: '#666060', cursor: 'pointer', fontSize: '0.7rem', padding: '2px', flexShrink: 0 }} onMouseEnter={e => e.currentTarget.style.color = '#e07070'} onMouseLeave={e => e.currentTarget.style.color = '#666060'}>✕</button>
              )}
            </div>
          ))}
          <div style={{ display: 'flex', gap: '8px', alignItems: 'center', marginTop: '8px' }}>
            <Avatar username={currentUser?.username} color={currentUser?.avatar_color} size={28} />
            <input
              value={commentText}
              onChange={e => setCommentText(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') submitComment(); }}
              placeholder="Add a comment..."
              style={{ flex: 1, background: '#141414', border: '1px solid #1e1e1e', padding: '8px 12px', color: '#f5f0eb', fontFamily: 'Jost, sans-serif', fontSize: '0.75rem', fontWeight: 300, outline: 'none', minWidth: 0 }}
            />
            <button
              onClick={submitComment}
              disabled={submitting || !commentText.trim()}
              style={{ background: 'none', border: '1px solid #c9a96e', color: '#c9a96e', fontFamily: 'Jost, sans-serif', fontSize: '0.6rem', letterSpacing: '0.15em', textTransform: 'uppercase', padding: isMobile ? '8px 10px' : '8px 14px', cursor: 'pointer', opacity: commentText.trim() ? 1 : 0.3, flexShrink: 0 }}
            >Post</button>
          </div>
        </div>
      )}
    </div>
  );
};

const Feed = ({ token, user, onNavigate }) => {
  const isMobile = useIsMobile();
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchUser, setSearchUser] = useState('');
  const [searchResult, setSearchResult] = useState(null);
  const [searchLoading, setSearchLoading] = useState(false);
  const [searchError, setSearchError] = useState('');

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { fetchFeed(); }, []);

  const fetchFeed = async () => {
    try {
      const res = await axios.get(`${API}/api/feed`, { headers: { Authorization: `Bearer ${token}` } });
      setEntries(res.data);
    } catch (err) { console.error(err); }
    setLoading(false);
  };

  const searchForUser = async () => {
    if (!searchUser.trim()) return;
    setSearchLoading(true); setSearchError(''); setSearchResult(null);
    try {
      const res = await axios.get(`${API}/api/users/${searchUser.trim()}`, { headers: { Authorization: `Bearer ${token}` } });
      setSearchResult(res.data);
    } catch (err) {
      setSearchError(err.response?.status === 404 ? 'User not found' : 'Something went wrong');
    }
    setSearchLoading(false);
  };

  const followUser = async (userId) => {
    try {
      await axios.post(`${API}/api/follows/${userId}`, {}, { headers: { Authorization: `Bearer ${token}` } });
      setSearchResult(prev => ({ ...prev, stats: { ...prev.stats, is_following: true, follower_count: parseInt(prev.stats.follower_count) + 1 } }));
      fetchFeed();
    } catch (err) { console.error(err); }
  };

  const unfollowUser = async (userId) => {
    try {
      await axios.delete(`${API}/api/follows/${userId}`, { headers: { Authorization: `Bearer ${token}` } });
      setSearchResult(prev => ({ ...prev, stats: { ...prev.stats, is_following: false, follower_count: parseInt(prev.stats.follower_count) - 1 } }));
      fetchFeed();
    } catch (err) { console.error(err); }
  };

  if (loading) return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <p style={{ fontFamily: 'Cormorant Garamond, serif', fontSize: '1.5rem', color: '#c9a96e', fontStyle: 'italic' }}>Loading feed...</p>
    </div>
  );

  return (
    <div style={{ minHeight: '100vh', padding: isMobile ? '88px 0 60px' : '100px 0 80px', background: '#0a0a0a' }}>
      <div style={{ maxWidth: '680px', margin: '0 auto', padding: isMobile ? '0 16px' : '0 24px' }}>

        {/* Header */}
        <div style={{ marginBottom: '32px', paddingBottom: '24px', borderBottom: '1px solid #1a1a1a' }}>
          <p style={{ fontFamily: 'Jost, sans-serif', fontSize: '0.65rem', letterSpacing: '0.4em', textTransform: 'uppercase', color: '#c9a96e', marginBottom: '10px' }}>Community</p>
          <h1 style={{ fontFamily: 'Cormorant Garamond, serif', fontSize: isMobile ? '2.2rem' : '3rem', fontWeight: 300, color: '#f5f0eb' }}>Feed</h1>
        </div>

        {/* Find People */}
        <div style={{ marginBottom: '32px', background: '#0f0f0f', border: '1px solid #1a1a1a', padding: isMobile ? '18px 16px' : '24px' }}>
          <p style={{ fontFamily: 'Jost, sans-serif', fontSize: '0.6rem', letterSpacing: '0.3em', textTransform: 'uppercase', color: '#7a7470', marginBottom: '14px' }}>Find People</p>
          <div style={{ display: 'flex' }}>
            <input
              value={searchUser}
              onChange={e => setSearchUser(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') searchForUser(); }}
              placeholder="Search by username..."
              style={{ flex: 1, background: '#141414', border: '1px solid #1e1e1e', borderRight: 'none', padding: '10px 14px', color: '#f5f0eb', fontFamily: 'Jost, sans-serif', fontSize: '0.8rem', fontWeight: 300, outline: 'none', minWidth: 0 }}
            />
            <button onClick={searchForUser} style={{ background: 'none', border: '1px solid #1e1e1e', color: '#c9a96e', fontFamily: 'Jost, sans-serif', fontSize: '0.6rem', letterSpacing: '0.2em', textTransform: 'uppercase', padding: '10px 16px', cursor: 'pointer', flexShrink: 0 }}>{searchLoading ? '...' : 'Search'}</button>
          </div>
          {searchError && <p style={{ fontFamily: 'Jost, sans-serif', fontSize: '0.72rem', color: '#6a3a3a', marginTop: '10px' }}>{searchError}</p>}
          {searchResult && (
            <div style={{ marginTop: '16px', padding: '14px', background: '#141414', border: '1px solid #1e1e1e' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '12px', flexWrap: isMobile ? 'wrap' : 'nowrap' }}>
                <div style={{ display: 'flex', gap: '12px', alignItems: 'center', flex: 1, minWidth: 0 }}>
                  <Avatar username={searchResult.user.username} color={searchResult.user.avatar_color} size={40} />
                  <div style={{ minWidth: 0 }}>
                    <p style={{ fontFamily: 'Jost, sans-serif', fontSize: '0.78rem', color: '#f5f0eb', marginBottom: '3px', letterSpacing: '0.1em', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{searchResult.user.username?.toUpperCase()}</p>
                    <p style={{ fontFamily: 'Jost, sans-serif', fontSize: '0.62rem', color: '#7a7470' }}>{searchResult.stats.fragrance_count} fragrances · {searchResult.stats.follower_count} followers</p>
                  </div>
                </div>
                <div style={{ display: 'flex', gap: '8px', flexShrink: 0 }}>
                  <button onClick={() => onNavigate(`/u/${searchResult.user.username}`)} style={{ background: 'none', border: '1px solid #666060', color: '#6b6460', fontFamily: 'Jost, sans-serif', fontSize: '0.6rem', letterSpacing: '0.15em', textTransform: 'uppercase', padding: '8px 12px', cursor: 'pointer' }}>View</button>
                  {searchResult.user.id !== user?.id && (
                    <button onClick={() => searchResult.stats.is_following ? unfollowUser(searchResult.user.id) : followUser(searchResult.user.id)} style={{ background: searchResult.stats.is_following ? 'none' : '#c9a96e', border: '1px solid #c9a96e', color: searchResult.stats.is_following ? '#c9a96e' : '#0a0a0a', fontFamily: 'Jost, sans-serif', fontSize: '0.6rem', letterSpacing: '0.15em', textTransform: 'uppercase', padding: '8px 12px', cursor: 'pointer' }}>{searchResult.stats.is_following ? 'Unfollow' : 'Follow'}</button>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Feed entries */}
        {entries.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '60px 0' }}>
            <p style={{ fontFamily: 'Cormorant Garamond, serif', fontSize: '1.8rem', fontWeight: 300, color: '#666060', fontStyle: 'italic', marginBottom: '16px' }}>Nothing here yet</p>
            <p style={{ fontFamily: 'Jost, sans-serif', fontSize: '0.75rem', color: '#666060', letterSpacing: '0.05em' }}>Follow other fragrance lovers to see their wears here</p>
          </div>
        ) : (
          <div>{entries.map(entry => <FeedEntry key={entry.id} entry={entry} token={token} currentUser={user} onNavigate={onNavigate} />)}</div>
        )}
      </div>
    </div>
  );
};

export default Feed;